/** @format */

import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { KeyValuePipe, TitleCasePipe } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';

import { HmfTextLinkComponent } from '@dsg-tech/homefield-angular';
import { HomefieldAccordionModule } from '@homefield';
import { SEOAttributes } from '@shared/data-access/interfaces';
import { Store } from '@ngrx/store';
import { selectProductListSEOAttributes } from '@plp/data-access/state';

@Component({
    selector: 'plp-components-seo-content',
    imports: [TitleCasePipe, KeyValuePipe, HmfTextLinkComponent, HomefieldAccordionModule],
    templateUrl: './seo-content.component.html',
    styleUrls: ['./seo-content.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SeoContentComponent implements OnInit, OnDestroy {
	private readonly destroy$ = new Subject<void>();

	seoHeader = '';
	featuredLinks = '';
	seoTabContentList;

	readonly productListSEOAttributes$ = this.store
		.select(selectProductListSEOAttributes)
		.pipe(takeUntil(this.destroy$));

	constructor(private store: Store) {}

	ngOnInit(): void {
		const categoryAttrHeadingMatchPattern = /\bSEO_TAB\d_HEADING/;
		const categoryAttrContentMatchPattern = /\bSEO_TAB\d_CONTENT/;
		this.productListSEOAttributes$.subscribe((productListSEOAttrData) => {
			if (productListSEOAttrData) {
				this.seoHeader = productListSEOAttrData['SEO_HEADLINE']
					? productListSEOAttrData['SEO_HEADLINE'].value
					: '';
				this.featuredLinks = productListSEOAttrData['SEO_FEATURED_LINKS']
					? JSON.parse(productListSEOAttrData['SEO_FEATURED_LINKS'].value)
					: '';
				// Check if any attribute values match seo tab heading or seo tab content.
				if (
					Object.keys(productListSEOAttrData).some(
						(key) =>
							categoryAttrHeadingMatchPattern.test(key) ||
							categoryAttrContentMatchPattern.test(key)
					)
				) {
					this.seoTabContentList = this.parseSEOTabContent(
						productListSEOAttrData,
						categoryAttrHeadingMatchPattern
					);
				}
			}
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	/**
	 * @description
	 * Iterates through productListSEOAttrData to search for a key matching the SEO Tab header.
	 * If a header match is found then we swap the HEADING text to CONTENT, in order to retrieve the content value as well.
	 * A List of objects is returned with the header and content as values.
	 */
	private parseSEOTabContent(productListSEOAttrData: SEOAttributes, headingPattern: RegExp): any[] {
		const seoTabResultList = [];
		Object.keys(productListSEOAttrData).forEach((key) => {
			// For each key, check if it matches the heading match pattern.
			if (key.search(headingPattern) === 0 && productListSEOAttrData[key]?.value) {
				const heading = productListSEOAttrData[key].value;
				// Swap Tab header text with tab content text, to retrieve content data.
				const contentKey = key.replace('HEADING', 'CONTENT');
				if (productListSEOAttrData[contentKey]) {
					seoTabResultList.push({
						header: heading,
						tabContent: productListSEOAttrData[contentKey].value
					});
				}
			}
		});
		return seoTabResultList;
	}

	trackByHeader(index: number, item: any): string {
		return index.toString() + item.header;
	}

	trackByKey(index: number, item: any): string {
		return index.toString() + item.key;
	}
}
